import type { AddWaitlistSubscriptionBodyCartItem } from '../../openapi/model/addWaitlistSubscriptionBodyCartItem';
import { TicketOptionsSuccessResponseResponseTicketOptionsItem } from '../../openapi/model/ticketOptionsSuccessResponseResponseTicketOptionsItem';
import { TicketTypesSuccessResponseResponseTicketTypesItem } from '../../openapi/model/ticketTypesSuccessResponseResponseTicketTypesItem';
import { definePrice } from '../helpers/calculateShowFees';
import { ICartItem, IGenericBookingFields } from '../hooks/useCart';

export type WaitlistPrefillPayload = {
    timeslotId: number;
    timeslotStartTime: string;
    cartItems: AddWaitlistSubscriptionBodyCartItem[];
    language: string | null;
    genericBookingFields: IGenericBookingFields | null;
};

const WAITLIST_QUERY_PARAMS = ['wlt', 'wls', 'wlc', 'wll', 'wlfn', 'wlln', 'wle', 'wlp'] as const;

export const getWaitlistPrefillFromUrl = (): WaitlistPrefillPayload | null => {
    const params = new URLSearchParams(globalThis.location.search);
    const timeslotIdRaw = params.get('wlt');
    const timeslotStartTime = params.get('wls');
    const cartRaw = params.get('wlc');
    const languageRaw = params.get('wll');
    const firstNameRaw = params.get('wlfn');
    const lastNameRaw = params.get('wlln');
    const emailRaw = params.get('wle');
    const phoneRaw = params.get('wlp');

    if (!timeslotIdRaw || !timeslotStartTime || !cartRaw) {
        return null;
    }

    const timeslotId = Number(timeslotIdRaw);
    if (!Number.isInteger(timeslotId) || timeslotId <= 0) {
        return null;
    }

    const cartItems = cartRaw
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean)
        .map((entry) => {
            const [ticketTypeIdRaw, ticketOptionIdRaw, quantityRaw] = entry.split(':');
            return {
                ticketTypeId: Number(ticketTypeIdRaw),
                ticketOptionId: Number(ticketOptionIdRaw),
                quantity: Number(quantityRaw),
            };
        })
        .filter(
            (item) =>
                Number.isInteger(item.ticketTypeId) &&
                item.ticketTypeId > 0 &&
                Number.isInteger(item.ticketOptionId) &&
                item.ticketOptionId > 0 &&
                Number.isInteger(item.quantity) &&
                item.quantity > 0,
        );

    if (cartItems.length === 0) {
        return null;
    }

    const genericBookingFields: IGenericBookingFields = {
        firstName: (firstNameRaw || '').trim(),
        lastName: (lastNameRaw || '').trim(),
        email: (emailRaw || '').trim(),
        phone: (phoneRaw || '').trim(),
    };
    const hasGenericBookingFieldPrefill = Object.values(genericBookingFields).some(
        (value) => value.length > 0,
    );

    return {
        timeslotId,
        timeslotStartTime,
        cartItems,
        language: (languageRaw || '').trim() || null,
        genericBookingFields: hasGenericBookingFieldPrefill ? genericBookingFields : null,
    };
};

export const clearWaitlistPrefillFromUrl = () => {
    const params = new URLSearchParams(globalThis.location.search);
    WAITLIST_QUERY_PARAMS.forEach((param) => params.delete(param));
    const q = params.toString();
    globalThis.history.replaceState(
        {},
        '',
        q ? `${globalThis.location.pathname}?${q}` : globalThis.location.pathname,
    );
};

export const buildWaitlistPrefilledCart = (
    prefill: WaitlistPrefillPayload,
    ticketTypes: TicketTypesSuccessResponseResponseTicketTypesItem[],
    ticketOptions: TicketOptionsSuccessResponseResponseTicketOptionsItem[],
): ICartItem[] => {
    const ticketTypeById = new Map(ticketTypes.map((ticketType) => [ticketType.id, ticketType]));
    const ticketOptionById = new Map(
        ticketOptions.map((ticketOption) => [ticketOption.id, ticketOption]),
    );

    return prefill.cartItems
        .map((item) => {
            const ticketType = ticketTypeById.get(item.ticketTypeId);
            const ticketOption = ticketOptionById.get(item.ticketOptionId);
            if (!ticketType || !ticketOption) {
                return null;
            }

            try {
                const ticketPrice = definePrice(ticketType.priceMatrix, ticketOption.name || '');
                return {
                    ticketTypeId: ticketType.id,
                    ticketTypeName: ticketType.name || '',
                    ticketTypeNameI18n: ticketType.nameI18n,
                    ticketOptionId: ticketOption.id,
                    ticketOptionName: ticketOption.name || '',
                    ticketOptionNameI18n: ticketOption.nameI18n,
                    price: ticketPrice,
                    basePrice: ticketPrice,
                    quantity: item.quantity,
                    customFields: ticketType.customFields || [],
                    canBuyAlone: Boolean(ticketType.canBuyAlone),
                    ageBand: ticketOption.ageBand ?? null,
                };
            } catch {
                return null;
            }
        })
        .filter((item): item is ICartItem => item !== null);
};
